import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Link de acesso a uma meeting (card 3241): `/dashboard/meetings/compartilhada/:token`. Troca o token
 * pelo acesso (até o prazo do link) e segue para o detalhe, sem deixar a rota do token no histórico.
 * Link revogado, vencido ou de quem não é do time interno mostra o motivo que o backend devolve.
 */
@Component({
  selector: 'app-meeting-shared-link',
  standalone: true,
  imports: [CommonModule, RouterModule, SharedModule],
  template: `
    <section class="shared-link">
      @if (erro(); as mensagem) {
        <i class="pi pi-lock shared-link__icon" aria-hidden="true"></i>
        <h2>Não foi possível abrir a meeting</h2>
        <p>{{ mensagem }}</p>
        <a class="shared-link__voltar" routerLink="/dashboard/meetings">Ir para Meetings</a>
      } @else {
        <app-loading />
        <p>Abrindo a meeting compartilhada...</p>
      }
    </section>
  `,
  styles: `
    .shared-link {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.6rem;
      max-width: 480px;
      margin: 4rem auto;
      padding: 2rem 1.5rem;
      border: 1px solid #eef0f2;
      border-radius: 16px;
      background: #fff;
      text-align: center;

      h2 {
        margin: 0;
        font-size: 1.1rem;
        color: #06142b;
      }

      p {
        margin: 0;
        font-size: 0.9rem;
        color: #6b7280;
      }
    }

    .shared-link__icon {
      font-size: 1.6rem;
      color: #9ca3af;
    }

    .shared-link__voltar {
      margin-top: 0.5rem;
      font-size: 0.9rem;
      color: #1d4ed8;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingSharedLinkComponent implements OnInit {
  protected readonly erro = signal<string | null>(null);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly meetingsService = inject(MeetingsHttpService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    // No SSR o VaultApiService não chama a API (devolve nulo): sem isto a página sairia do servidor com
    // "Link de acesso não encontrado." e só depois o navegador abriria a meeting.
    if (!isPlatformBrowser(this.platformId)) return;
    const token = this.route.snapshot.paramMap.get('token') ?? '';
    this.meetingsService
      .openSharedMeeting(token)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: aberto => {
          if (!aberto?.meetingId) {
            this.erro.set('Link de acesso não encontrado.');
            return;
          }
          this.router.navigate(['/dashboard/meetings', aberto.meetingId], { replaceUrl: true });
        },
        error: erro => this.erro.set(mensagemDoErro(erro, 'Tente de novo em alguns instantes.')),
      });
  }
}
